<template>
  <div class="mass-campaign-page">
    <vx-card class="mb-6">
      <div class="d-flex flex-wrap" style="gap: 12px; align-items: center;">
        <vs-button v-if="!showCreateFlow" color="primary" @click="startNewBatch">+ Adicionar</vs-button>
        <vs-button v-else type="border" color="warning" @click="showCreateFlow = false">Fechar formulário</vs-button>
      </div>
    </vx-card>

    <vx-card v-if="showCreateFlow" title="Nova Criação em Massa" class="mb-6">
      <mass-campaign-wizard-progress :current-step="currentStep" />

      <mass-campaign-step-one
        v-if="currentStep === 1"
        :form="form"
        :reference-tenant-id="referenceTenantId"
        :reference-tenant-options="referenceTenantOptions"
        :reference-tenant-label="referenceTenantName"
        :reference-loaded="referenceOptionsLoaded"
        :loading-options="loadingOptions"
        @selected-tenant="onTenantsSelected"
        @update-reference-tenant-id="referenceTenantId = $event"
        @load-options="loadReferenceOptions"
      />

      <mass-campaign-step-two
        v-if="currentStep === 2"
        :form="form"
        :selector-options="selectorOptions"
        :selected-suggestions="selectedSuggestions"
        :tenant-instance-rows="tenantInstanceRows"
        :tenant-customer-group-rows="tenantCustomerGroupRows"
        :loading-tenant-instances="loadingTenantInstances"
        :selected-template-preview="selectedTemplatePreview"
        :template-param-placeholders="templateParamPlaceholders"
        :template-param-values="officialParamsValues"
        :reference-group-tag-options="referenceGroupTagOptions"
        :reference-group-has-diff-tag-list="referenceGroupHasDiffTagList"
        @update-suggestion="updateSuggestion"
        @apply-instance-suggestion="applyInstanceSuggestion"
        @apply-customer-group-suggestion="applyCustomerGroupSuggestion"
        @apply-message-template-suggestion="applyMessageTemplateSuggestion"
        @apply-chat-flow-suggestion="applyChatFlowSuggestion"
        @set-tenant-instance-override="setTenantInstanceOverride"
        @set-tenant-customer-group-override="setTenantCustomerGroupOverride"
        @update-template-param-values="officialParamsValues = $event"
      />

      <mass-campaign-step-three
        v-if="currentStep === 3"
        :form="form"
        :is-official-instance="isReferenceInstanceOfficial()"
        :required-template-params-count="requiredTemplateParamsCount"
        :required-template-header-format="requiredTemplateHeaderFormat"
        :upload-api-url="uploadApiUrl"
        :upload-headers="uploadHeaders"
        :upload-data="uploadData"
        :template-has-carousel="templateHasCarousel"
        :official-params-values="officialParamsValues"
        :date-time-picker-config="dateTimePickerConfig"
        :loading-dry-run="loadingDryRun"
        :loading-execute="loadingExecute"
        :can-execute-batch="canExecuteBatch"
        :dry-run-batch="dryRunBatch"
        @run-dry-run="runDryRun"
        @execute-batch="executeBatch"
      />

      <div class="wizard-footer mt-5">
        <vs-button type="border" color="danger" @click="cancelCreateFlow">Cancelar</vs-button>
        <div class="d-flex" style="gap: 10px;">
          <vs-button v-if="currentStep > 1" type="border" color="warning" @click="goToPreviousStep">Voltar</vs-button>
          <vs-button v-if="currentStep < 3" color="primary" @click="goToNextStep" :disabled="isNextButtonDisabled">Próximo</vs-button>
        </div>
      </div>
      <p v-if="currentStep < 3 && nextStepBlockingReason" class="wizard-hint mt-2 mb-0">{{ nextStepBlockingReason }}</p>
    </vx-card>

    <mass-campaign-history-section
      :history="history"
      :filters="filters"
      :history-status-options="historyStatusOptions"
      :selected-batch-detail="selectedBatchDetail"
      :date-picker-config="datePickerConfig"
      @fetch-history="fetchHistory"
      @reset-history-filters="resetHistoryFilters"
      @show-batch-detail="showBatchDetail"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import { detectMediaFormatFromUrl } from '@/utils';
import MassCampaignWizardProgress from './components/MassCampaignWizardProgress.vue';
import MassCampaignStepOne from './components/MassCampaignStepOne.vue';
import MassCampaignStepTwo from './components/MassCampaignStepTwo.vue';
import MassCampaignStepThree from './components/MassCampaignStepThree.vue';
import MassCampaignHistorySection from './components/MassCampaignHistorySection.vue';

const initialFormState = () => ({
  name: '',
  tenantIds: [],
  instanceName: '',
  customerGroupName: '',
  messageTemplateName: '',
  useChatFlow: false,
  chatFlowDescription: '',
  startAt: null,
  finishAt: null,
  message: '',
  firstMessage: '',
  imageUrl: '',
  imageType: null,
  imageCaption: '',
  imageDescription: '',
  sendOptOut: true,
  setCaption: false,
  updateAllInSheet: false,
  isHumanized: false,
});

const initialSelectorOptions = () => ({
  instances: [],
  customerGroups: [],
  messageTemplates: [],
  chatFlows: [],
});

const initialSelectedSuggestions = () => ({
  instanceId: null,
  customerGroupId: null,
  messageTemplateId: null,
  chatFlowId: null,
});

export default {
  name: 'MassCampaigns',
  components: {
    MassCampaignWizardProgress,
    MassCampaignStepOne,
    MassCampaignStepTwo,
    MassCampaignStepThree,
    MassCampaignHistorySection,
  },
  data() {
    return {
      showCreateFlow: false,
      currentStep: 1,
      loadingDryRun: false,
      loadingExecute: false,
      loadingOptions: false,
      dryRunBatch: null,
      selectedBatchDetail: null,
      referenceTenantId: null,
      lastLoadedTenantName: null,
      referenceTemplateFilteredInstanceId: null,
      optionsCacheByTenant: {},
      loadingTenantInstances: false,
      tenantInstanceOverridesByTenantId: {},
      tenantCustomerGroupOverridesByTenantId: {},
      selectorOptions: initialSelectorOptions(),
      selectedSuggestions: initialSelectedSuggestions(),
      history: {
        pagination_page: 1,
        pagination_limit: 10,
        total_pages: 0,
        total_items: 0,
        items: [],
      },
      filters: {
        status: '',
        from: null,
        to: null,
      },
      historyStatusOptions: [
        { label: 'Todos', value: '' },
        { label: 'validated', value: 'validated' },
        { label: 'queued', value: 'queued' },
        { label: 'running', value: 'running' },
        { label: 'completed', value: 'completed' },
        { label: 'completed_with_errors', value: 'completed_with_errors' },
        { label: 'failed', value: 'failed' },
      ],
      form: initialFormState(),
      officialParamsValues: [],
    };
  },
  computed: {
    ...mapGetters(['token', 'tenants']),
    dateTimePickerConfig() {
      return {
        enableTime: true,
        noCalendar: false,
        dateFormat: 'Y-m-d H:i:S',
        time_24hr: true,
        locale: Portuguese,
      };
    },
    datePickerConfig() {
      return {
        enableTime: false,
        noCalendar: false,
        dateFormat: 'Y-m-d',
        locale: Portuguese,
      };
    },
    uploadApiUrl() {
      const baseUrl = `${localStorage.getItem('baseUrl') || process.env.VUE_APP_API_BASE_URL || ''}`.replace(/\/+$/, '');
      return `${baseUrl}/api/file/upload`;
    },
    uploadHeaders() {
      const accessToken = `${this.token || localStorage.getItem('accessToken') || ''}`.trim();
      if (!accessToken) {
        return {};
      }

      return {
        Authorization: `Bearer ${accessToken}`,
      };
    },
    uploadData() {
      return {
        tenantName: this.referenceTenantName || '',
        isOfficial: this.isReferenceInstanceOfficial(),
      };
    },
    canExecuteBatch() {
      if (!this.dryRunBatch) {
        return false;
      }

      return (this.dryRunBatch.ready_tenants || 0) > 0 && ['validated', 'failed'].includes(this.dryRunBatch.status);
    },
    referenceTenantOptions() {
      if (!Array.isArray(this.form.tenantIds) || !this.form.tenantIds.length) {
        return [];
      }

      const selectedIds = [...new Set(
        this.form.tenantIds
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      )];

      return selectedIds.map((tenantId) => {
        const tenant = Array.isArray(this.tenants)
          ? this.tenants.find((item) => Number(item && item.id) === tenantId)
          : null;

        const tenantName = tenant ? this.parseTenantNameFromFqdn(tenant.fqdn) : null;

        return {
          id: tenantId,
          label: tenantName || `tenant_${tenantId}`,
        };
      });
    },
    selectedTenantRows() {
      return this.referenceTenantOptions.map((item) => ({
        tenantId: Number(item.id),
        tenantLabel: item.label,
        tenantName: this.resolveTenantNameById(item.id),
      }));
    },
    tenantInstanceRows() {
      const expectedOfficial = this.referenceInstanceOfficialFlag;
      const referenceTenantId = Number(this.referenceTenantId);

      return this.selectedTenantRows.map((row) => {
        const cached = row.tenantName && this.optionsCacheByTenant[row.tenantName]
          ? this.optionsCacheByTenant[row.tenantName]
          : null;
        const cacheLoaded = !!cached;
        const tenantInstances = cached && Array.isArray(cached.instances) ? cached.instances : [];

        const compatibleOptions = tenantInstances.filter((item) => {
          if (!item || item.activated === false) {
            return false;
          }

          if (typeof expectedOfficial === 'boolean') {
            return !!item.isOfficial === expectedOfficial;
          }

          return true;
        });

        const override = this.tenantInstanceOverridesByTenantId[row.tenantId] || {};
        const selectedInstanceId = Number(override.instanceId);
        const selectedOption = Number.isNaN(selectedInstanceId)
          ? null
          : compatibleOptions.find((item) => Number(item.id) === selectedInstanceId) || null;

        const defaultSingleOption = compatibleOptions.length === 1 ? compatibleOptions[0] : null;
        const resolvedOption = selectedOption || defaultSingleOption || null;

        return {
          tenantId: row.tenantId,
          tenantLabel: row.tenantLabel,
          tenantName: row.tenantName,
          isReferenceTenant: !Number.isNaN(referenceTenantId) && Number(row.tenantId) === referenceTenantId,
          options: compatibleOptions,
          optionsCount: compatibleOptions.length,
          selectedInstanceId: selectedOption ? Number(selectedOption.id) : null,
          selectedInstanceName: resolvedOption ? resolvedOption.name : '',
          selectedIsOfficial: resolvedOption ? !!resolvedOption.isOfficial : null,
          cacheLoaded,
          requiredSelection: cacheLoaded && compatibleOptions.length > 1,
          unresolved: cacheLoaded && compatibleOptions.length === 0,
        };
      });
    },
    tenantCustomerGroupRows() {
      const referenceTenantId = Number(this.referenceTenantId);

      return this.selectedTenantRows.map((row) => {
        const cached = row.tenantName && this.optionsCacheByTenant[row.tenantName]
          ? this.optionsCacheByTenant[row.tenantName]
          : null;
        const cacheLoaded = !!cached;
        const tenantCustomerGroups = cached && Array.isArray(cached.customerGroups) ? cached.customerGroups : [];

        const override = this.tenantCustomerGroupOverridesByTenantId[row.tenantId] || {};
        const selectedGroupId = Number(override.customerGroupId);
        const selectedOption = Number.isNaN(selectedGroupId)
          ? null
          : tenantCustomerGroups.find((item) => Number(item.id) === selectedGroupId) || null;

        const defaultSingleOption = tenantCustomerGroups.length === 1 ? tenantCustomerGroups[0] : null;
        const resolvedOption = selectedOption || defaultSingleOption || null;

        return {
          tenantId: row.tenantId,
          tenantLabel: row.tenantLabel,
          tenantName: row.tenantName,
          isReferenceTenant: !Number.isNaN(referenceTenantId) && Number(row.tenantId) === referenceTenantId,
          options: tenantCustomerGroups,
          optionsCount: tenantCustomerGroups.length,
          selectedCustomerGroupId: selectedOption ? Number(selectedOption.id) : null,
          selectedCustomerGroupName: resolvedOption ? resolvedOption.name : '',
          selectedTagList: resolvedOption && Array.isArray(resolvedOption.tagList) ? resolvedOption.tagList : [],
          hasDiffTagList: !!(resolvedOption && resolvedOption.hasDiffTagList),
          cacheLoaded,
          requiredSelection: cacheLoaded && tenantCustomerGroups.length > 1,
          unresolved: cacheLoaded && tenantCustomerGroups.length === 0,
        };
      });
    },
    tenantInstanceMappingBlockingReason() {
      if (this.loadingTenantInstances) {
        return 'Carregando identificadores das dashes selecionadas.';
      }

      const pendingLoad = this.tenantInstanceRows.find((row) => !row.cacheLoaded);
      if (pendingLoad) {
        return 'Aguarde o carregamento das conexões por dash.';
      }

      const unresolvedRow = this.tenantInstanceRows.find((row) => row.unresolved);
      if (unresolvedRow) {
        return `O tenant ${unresolvedRow.tenantLabel} não possui conexão ativa compatível com o modelo selecionado.`;
      }

      const missingSelection = this.tenantInstanceRows.find((row) => row.requiredSelection && !row.selectedInstanceName);
      if (missingSelection) {
        return `Selecione a conexão do tenant ${missingSelection.tenantLabel}.`;
      }

      return null;
    },
    tenantCustomerGroupMappingBlockingReason() {
      if (this.loadingTenantInstances) {
        return 'Carregando identificadores das dashes selecionadas.';
      }

      const pendingLoad = this.tenantCustomerGroupRows.find((row) => !row.cacheLoaded);
      if (pendingLoad) {
        return 'Aguarde o carregamento dos grupos de clientes por dash.';
      }

      const unresolvedRow = this.tenantCustomerGroupRows.find((row) => row.unresolved);
      if (unresolvedRow) {
        return `O tenant ${unresolvedRow.tenantLabel} não possui grupos de clientes disponíveis.`;
      }

      const missingSelection = this.tenantCustomerGroupRows.find((row) => row.requiredSelection && !row.selectedCustomerGroupName);
      if (missingSelection) {
        return `Selecione o grupo de clientes do tenant ${missingSelection.tenantLabel}.`;
      }

      return null;
    },
    selectedReferenceCustomerGroupOption() {
      const selectedGroupId = Number(this.selectedSuggestions.customerGroupId);
      if (Number.isNaN(selectedGroupId)) {
        return null;
      }

      return this.selectorOptions.customerGroups.find((item) => Number(item.id) === selectedGroupId) || null;
    },
    referenceGroupHasDiffTagList() {
      const source = this.selectedReferenceCustomerGroupOption;
      return !!(source && source.hasDiffTagList);
    },
    referenceGroupTagOptions() {
      const source = this.selectedReferenceCustomerGroupOption;
      const tags = source && Array.isArray(source.tagList) ? source.tagList : [];
      const normalized = tags
        .map((item) => `${item || ''}`.trim())
        .filter((item) => item !== '');

      if (!normalized.includes('Nome')) {
        normalized.unshift('Nome');
      }

      return [...new Set(normalized)];
    },
    selectedTemplateOption() {
      const selectedTemplateId = Number(this.selectedSuggestions.messageTemplateId);
      if (Number.isNaN(selectedTemplateId)) {
        return null;
      }

      return this.selectorOptions.messageTemplates.find((item) => Number(item.id) === selectedTemplateId) || null;
    },
    selectedTemplatePreview() {
      if (!this.selectedTemplateOption) {
        return null;
      }

      return this.selectedTemplateOption.preview || null;
    },
    templateParamPlaceholders() {
      if (!this.selectedTemplateOption || !Array.isArray(this.selectedTemplateOption.bodyPlaceholders)) {
        return [];
      }

      return this.selectedTemplateOption.bodyPlaceholders;
    },
    requiredTemplateParamsCount() {
      return Number((this.selectedTemplateOption && this.selectedTemplateOption.bodyParamsCount) || 0);
    },
    requiredTemplateHeaderFormat() {
      const selectedFormat = `${(this.selectedTemplateOption && this.selectedTemplateOption.headerFormat) || ''}`.trim().toUpperCase();
      if (selectedFormat !== '') {
        return selectedFormat;
      }

      const previewFormat = `${(((this.selectedTemplatePreview || {}).header || {}).format || '')}`.trim().toUpperCase();
      return previewFormat === '' ? null : previewFormat;
    },
    templateHasCarousel() {
      return Number((this.selectedTemplateOption && this.selectedTemplateOption.cardsCount) || 0) > 0;
    },
    selectedReferenceInstanceOption() {
      const selectedInstanceId = Number(this.selectedSuggestions.instanceId);
      if (Number.isNaN(selectedInstanceId)) {
        return null;
      }

      return this.selectorOptions.instances.find((item) => Number(item.id) === selectedInstanceId) || null;
    },
    referenceInstanceOfficialFlag() {
      if (!this.selectedReferenceInstanceOption) {
        return null;
      }

      return !!this.selectedReferenceInstanceOption.isOfficial;
    },
    referenceTenantName() {
      return this.resolveTenantNameById(this.referenceTenantId) || null;
    },
    referenceOptionsLoaded() {
      if (!this.referenceTenantName) {
        return false;
      }

      return this.lastLoadedTenantName === this.referenceTenantName;
    },
    canAdvanceStepOne() {
      return Array.isArray(this.form.tenantIds)
        && this.form.tenantIds.length > 0
        && !!this.referenceTenantId
        && this.referenceOptionsLoaded;
    },
    canAdvanceStepTwo() {
      const campaignName = `${this.form.name || ''}`.trim();
      const instanceName = `${this.form.instanceName || ''}`.trim();
      const customerGroupName = `${this.form.customerGroupName || ''}`.trim();
      const chatFlowDescription = `${this.form.chatFlowDescription || ''}`.trim();
      const templateName = `${this.form.messageTemplateName || ''}`.trim();

      if (!campaignName || !instanceName || !customerGroupName) {
        return false;
      }

      if (this.form.useChatFlow && !chatFlowDescription) {
        return false;
      }

      if (this.isReferenceInstanceOfficial() && (!templateName || !this.selectedSuggestions.messageTemplateId)) {
        return false;
      }

      if (this.isReferenceInstanceOfficial() && this.requiredTemplateParamsCount > 0) {
        if (!this.referenceGroupTagOptions.length) {
          return false;
        }

        const values = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];
        if (values.length !== this.requiredTemplateParamsCount) {
          return false;
        }

        const hasEmpty = values.some((item) => `${item || ''}`.trim() === '');
        if (hasEmpty) {
          return false;
        }
      }

      if (this.tenantInstanceMappingBlockingReason) {
        return false;
      }

      if (this.tenantCustomerGroupMappingBlockingReason) {
        return false;
      }

      return true;
    },
    nextStepBlockingReason() {
      if (this.currentStep === 1) {
        if (!Array.isArray(this.form.tenantIds) || !this.form.tenantIds.length) {
          return 'Selecione pelo menos uma dash para continuar.';
        }

        if (!this.referenceTenantId) {
          return 'Selecione o tenant de referência (modelo).';
        }

        if (!this.referenceOptionsLoaded) {
          return 'Aguarde o carregamento das sugestões do tenant modelo.';
        }
      }

      if (this.currentStep === 2) {
        if (!`${this.form.name || ''}`.trim()) {
          return 'Preencha o nome da campanha.';
        }

        if (!`${this.form.instanceName || ''}`.trim()) {
          return 'Selecione a instância para continuar.';
        }

        if (!`${this.form.customerGroupName || ''}`.trim()) {
          return 'Selecione o grupo de clientes para continuar.';
        }

        if (this.form.useChatFlow && !`${this.form.chatFlowDescription || ''}`.trim()) {
          return 'Com fluxo ativo, a descrição do fluxo é obrigatória.';
        }

        if (this.isReferenceInstanceOfficial() && !this.selectedSuggestions.messageTemplateId) {
          return 'Conexão Cloud exige modelo selecionado.';
        }

        if (this.isReferenceInstanceOfficial() && this.requiredTemplateParamsCount > 0) {
          if (!this.referenceGroupTagOptions.length) {
            return 'O grupo de clientes de referência não possui campos para mapear as variáveis do modelo.';
          }

          const values = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];
          const hasEmpty = values.length !== this.requiredTemplateParamsCount
            || values.some((item) => `${item || ''}`.trim() === '');

          if (hasEmpty) {
            return `Mapeie os ${this.requiredTemplateParamsCount} parâmetros do modelo para campos do grupo de clientes.`;
          }
        }

        if (this.tenantInstanceMappingBlockingReason) {
          return this.tenantInstanceMappingBlockingReason;
        }

        if (this.tenantCustomerGroupMappingBlockingReason) {
          return this.tenantCustomerGroupMappingBlockingReason;
        }
      }

      return null;
    },
    isNextButtonDisabled() {
      if (this.currentStep === 1) {
        return !this.canAdvanceStepOne;
      }

      if (this.currentStep === 2) {
        return !this.canAdvanceStepTwo;
      }

      return false;
    },
  },
  methods: {
    startNewBatch() {
      this.resetCreateFlowState();
      this.showCreateFlow = true;
      this.currentStep = 1;
    },
    cancelCreateFlow() {
      this.resetCreateFlowState();
      this.showCreateFlow = false;
      this.currentStep = 1;
    },
    async goToNextStep() {
      try {
        if (this.currentStep === 1) {
          this.validateStepOne();
          await this.loadSelectedTenantsInstanceOptions();
        }

        if (this.currentStep === 2) {
          this.validateStepTwo();
        }

        if (this.currentStep < 3) {
          this.currentStep += 1;
        }
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Não foi possível avançar.');
        this.$vs.notify({ color: 'warning', title: 'Atenção', text: message });
      }
    },
    goToPreviousStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    updateSuggestion(payload) {
      if (!payload || typeof payload.key !== 'string') {
        return;
      }

      this.$set(this.selectedSuggestions, payload.key, payload.value);
    },
    setTenantInstanceOverride(payload) {
      if (!payload) {
        return;
      }

      const tenantId = Number(payload.tenantId);
      const instanceId = Number(payload.instanceId);
      if (Number.isNaN(tenantId)) {
        return;
      }

      const current = { ...(this.tenantInstanceOverridesByTenantId || {}) };
      current[tenantId] = {
        instanceId: Number.isNaN(instanceId) ? null : instanceId,
      };

      this.tenantInstanceOverridesByTenantId = current;
    },
    setTenantCustomerGroupOverride(payload) {
      if (!payload) {
        return;
      }

      const tenantId = Number(payload.tenantId);
      const customerGroupId = Number(payload.customerGroupId);
      if (Number.isNaN(tenantId)) {
        return;
      }

      const current = { ...(this.tenantCustomerGroupOverridesByTenantId || {}) };
      current[tenantId] = {
        customerGroupId: Number.isNaN(customerGroupId) ? null : customerGroupId,
      };

      this.tenantCustomerGroupOverridesByTenantId = current;
    },
    validateStepTwoMappings() {
      const mappingReason = this.tenantInstanceMappingBlockingReason || this.tenantCustomerGroupMappingBlockingReason;
      if (mappingReason) {
        throw new Error(mappingReason);
      }
    },
    validateStepOne() {
      if (!this.form.tenantIds.length) {
        throw new Error('Selecione ao menos uma dash.');
      }

      if (!this.referenceTenantId) {
        throw new Error('Selecione o tenant de referência.');
      }

      if (!this.referenceOptionsLoaded) {
        throw new Error('Carregue as sugestões do tenant modelo antes de avançar.');
      }
    },
    validateStepTwo() {
      if (!this.form.name) {
        throw new Error('Nome da campanha é obrigatório.');
      }

      if (!this.form.instanceName) {
        throw new Error('Nome da instância é obrigatório.');
      }

      if (!this.form.customerGroupName) {
        throw new Error('Nome do grupo de clientes é obrigatório.');
      }

      if (this.form.useChatFlow && !this.form.chatFlowDescription) {
        throw new Error('Descrição do fluxo é obrigatória quando envio por fluxo estiver ativo.');
      }

      if (this.isReferenceInstanceOfficial() && !this.form.messageTemplateName) {
        throw new Error('Modelo de Mensagem é obrigatório para conexões Cloud.');
      }

      if (this.isReferenceInstanceOfficial() && !this.selectedSuggestions.messageTemplateId) {
        throw new Error('Selecione o modelo na lista de sugestões do tenant modelo.');
      }

      if (this.isReferenceInstanceOfficial() && this.requiredTemplateParamsCount > 0 && !this.referenceGroupTagOptions.length) {
        throw new Error('O grupo de clientes de referência não possui campos para mapear as variáveis do modelo.');
      }

      this.validateStepTwoMappings();
    },
    resetCreateFlowState() {
      this.loadingDryRun = false;
      this.loadingExecute = false;
      this.loadingOptions = false;
      this.loadingTenantInstances = false;
      this.dryRunBatch = null;
      this.referenceTenantId = null;
      this.lastLoadedTenantName = null;
      this.referenceTemplateFilteredInstanceId = null;
      this.optionsCacheByTenant = {};
      this.tenantInstanceOverridesByTenantId = {};
      this.tenantCustomerGroupOverridesByTenantId = {};
      this.selectorOptions = initialSelectorOptions();
      this.selectedSuggestions = initialSelectedSuggestions();
      this.form = initialFormState();
      this.officialParamsValues = [];
    },
    extractErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return fallback;
    },
    parseTenantNameFromFqdn(fqdn) {
      if (!fqdn || typeof fqdn !== 'string') {
        return null;
      }

      const parts = fqdn.split('.');
      return parts[0] || null;
    },
    resolveTenantNameById(tenantId) {
      if (!Array.isArray(this.tenants)) {
        return null;
      }

      const parsedTenantId = Number(tenantId);
      if (Number.isNaN(parsedTenantId)) {
        return null;
      }

      const selectedTenant = this.tenants.find((tenant) => Number(tenant.id) === parsedTenantId);
      return selectedTenant ? this.parseTenantNameFromFqdn(selectedTenant.fqdn) : null;
    },
    syncTenantInstanceOverrides() {
      const expectedOfficial = this.referenceInstanceOfficialFlag;
      const referenceTenantId = Number(this.referenceTenantId);
      const referenceInstanceId = Number(this.selectedSuggestions.instanceId);
      const next = {};

      this.selectedTenantRows.forEach((row) => {
        if (!row || !row.tenantName) {
          return;
        }

        const cached = this.optionsCacheByTenant[row.tenantName];
        const previous = this.tenantInstanceOverridesByTenantId[row.tenantId] || {};

        if (!cached) {
          const previousId = Number(previous.instanceId);
          next[row.tenantId] = {
            instanceId: Number.isNaN(previousId) ? null : previousId,
          };
          return;
        }

        const instances = cached && Array.isArray(cached.instances) ? cached.instances : [];
        const compatibleOptions = instances.filter((item) => {
          if (!item || item.activated === false) {
            return false;
          }

          if (typeof expectedOfficial === 'boolean') {
            return !!item.isOfficial === expectedOfficial;
          }

          return true;
        });

        let selectedId = Number(previous.instanceId);
        if (Number.isNaN(selectedId)) {
          selectedId = null;
        }

        if (selectedId !== null) {
          const hasOption = compatibleOptions.some((item) => Number(item.id) === selectedId);
          if (!hasOption) {
            selectedId = null;
          }
        }

        if (
          !Number.isNaN(referenceTenantId)
          && row.tenantId === referenceTenantId
          && !Number.isNaN(referenceInstanceId)
        ) {
          const hasReferenceOption = compatibleOptions.some((item) => Number(item.id) === referenceInstanceId);
          if (hasReferenceOption) {
            selectedId = referenceInstanceId;
          }
        }

        if (selectedId === null && compatibleOptions.length === 1) {
          selectedId = Number(compatibleOptions[0].id);
        }

        next[row.tenantId] = { instanceId: selectedId };
      });

      this.tenantInstanceOverridesByTenantId = next;
    },
    syncTenantCustomerGroupOverrides() {
      const referenceTenantId = Number(this.referenceTenantId);
      const referenceCustomerGroupId = Number(this.selectedSuggestions.customerGroupId);
      const next = {};

      this.selectedTenantRows.forEach((row) => {
        if (!row || !row.tenantName) {
          return;
        }

        const cached = this.optionsCacheByTenant[row.tenantName];
        const previous = this.tenantCustomerGroupOverridesByTenantId[row.tenantId] || {};

        if (!cached) {
          const previousId = Number(previous.customerGroupId);
          next[row.tenantId] = {
            customerGroupId: Number.isNaN(previousId) ? null : previousId,
          };
          return;
        }

        const customerGroups = Array.isArray(cached.customerGroups) ? cached.customerGroups : [];

        let selectedId = Number(previous.customerGroupId);
        if (Number.isNaN(selectedId)) {
          selectedId = null;
        }

        if (selectedId !== null) {
          const hasOption = customerGroups.some((item) => Number(item.id) === selectedId);
          if (!hasOption) {
            selectedId = null;
          }
        }

        if (
          !Number.isNaN(referenceTenantId)
          && row.tenantId === referenceTenantId
          && !Number.isNaN(referenceCustomerGroupId)
        ) {
          const hasReferenceOption = customerGroups.some((item) => Number(item.id) === referenceCustomerGroupId);
          if (hasReferenceOption) {
            selectedId = referenceCustomerGroupId;
          }
        }

        if (selectedId === null && customerGroups.length === 1) {
          selectedId = Number(customerGroups[0].id);
        }

        next[row.tenantId] = { customerGroupId: selectedId };
      });

      this.tenantCustomerGroupOverridesByTenantId = next;
    },
    sanitizeOfficialParamsValues() {
      if (!this.isReferenceInstanceOfficial()) {
        this.officialParamsValues = [];
        return;
      }

      const requiredCount = this.requiredTemplateParamsCount;
      if (requiredCount <= 0) {
        this.officialParamsValues = [];
        return;
      }

      const validTags = new Set(this.referenceGroupTagOptions);
      const current = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];
      this.officialParamsValues = Array.from({ length: requiredCount }, (_, index) => {
        const value = `${current[index] || ''}`.trim();
        return validTags.has(value) ? value : '';
      });
    },
    extractTemplatePreview(components) {
      const preview = {
        header: null,
        body: null,
        footer: null,
        buttons: [],
      };

      let normalizedComponents = components;
      if (typeof normalizedComponents === 'string') {
        try {
          normalizedComponents = JSON.parse(normalizedComponents);
        } catch (error) {
          return preview;
        }
      }

      if (!Array.isArray(normalizedComponents)) {
        return preview;
      }

      normalizedComponents.forEach((component) => {
        const componentArray = component || {};
        const type = `${componentArray.type || ''}`.trim().toUpperCase();

        if (type === 'HEADER') {
          preview.header = {
            format: `${componentArray.format || 'TEXT'}`.trim().toUpperCase(),
            text: `${componentArray.text || ''}`,
          };
        }

        if (type === 'BODY') {
          preview.body = `${componentArray.text || ''}`;
        }

        if (type === 'FOOTER') {
          preview.footer = `${componentArray.text || ''}`;
        }

        if (type === 'BUTTONS') {
          preview.buttons = Array.isArray(componentArray.buttons)
            ? componentArray.buttons.map((item) => ({
              type: `${item.type || ''}`.trim().toUpperCase(),
              text: `${item.text || ''}`.trim(),
            })).filter((item) => item.text !== '')
            : [];
        }
      });

      return preview;
    },
    extractTemplateBodyPlaceholders(bodyText) {
      const text = `${bodyText || ''}`;
      const regex = /\{\{\s*([^}]+?)\s*\}\}/g;
      const placeholders = [];
      let match = regex.exec(text);

      while (match) {
        const value = `${match[1] || ''}`.trim();
        if (value !== '') {
          placeholders.push(value);
        }
        match = regex.exec(text);
      }

      if (!placeholders.length) {
        return [];
      }

      return placeholders;
    },
    extractTemplateCardsCount(components) {
      let normalizedComponents = components;
      if (typeof normalizedComponents === 'string') {
        try {
          normalizedComponents = JSON.parse(normalizedComponents);
        } catch (error) {
          return 0;
        }
      }

      if (!Array.isArray(normalizedComponents)) {
        return 0;
      }

      for (const component of normalizedComponents) {
        const componentArray = component || {};
        const type = `${componentArray.type || ''}`.trim().toUpperCase();
        if (type !== 'CAROUSEL') {
          continue;
        }

        const cards = Array.isArray(componentArray.cards) ? componentArray.cards : [];
        return cards.length;
      }

      return 0;
    },
    resolveCurrentImageMediaFormat() {
      const explicitFormat = `${this.form.imageType || ''}`.trim().toUpperCase();
      if (explicitFormat !== '') { return explicitFormat; }
      return detectMediaFormatFromUrl(this.form.imageUrl);
    },
    async loadSelectedTenantsInstanceOptions() {
      if (!Array.isArray(this.form.tenantIds) || !this.form.tenantIds.length) {
        return;
      }

      this.loadingTenantInstances = true;

      try {
        for (const row of this.selectedTenantRows) {
          if (!row || !row.tenantName) {
            continue;
          }

          if (this.optionsCacheByTenant[row.tenantName]) {
            continue;
          }

          const response = await this.$store.dispatch('campaignMassOptions', {
            tenantName: row.tenantName,
            token: this.token,
          });

          const normalized = this.normalizeTenantOptionsPayload(response.data || {});
          this.optionsCacheByTenant = {
            ...this.optionsCacheByTenant,
            [row.tenantName]: normalized,
          };
        }

        this.syncTenantInstanceOverrides();
        this.syncTenantCustomerGroupOverrides();
        this.sanitizeOfficialParamsValues();
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao carregar identificadores dos tenants selecionados.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        this.loadingTenantInstances = false;
      }
    },
    normalizeTenantOptionsPayload(data) {
      const instances = Array.isArray(data.instances) ? data.instances : [];
      const customerGroups = Array.isArray(data.customer_groups) ? data.customer_groups : [];
      const messageTemplates = Array.isArray(data.message_templates) ? data.message_templates : [];
      const chatFlows = Array.isArray(data.chat_flows) ? data.chat_flows : [];

      const normalizedTemplates = messageTemplates
        .map((template) => {
          const item = template || {};
          const id = Number(item.id || 0);
          const name = `${item.name || ''}`.trim();
          if (id <= 0 || name === '') {
            return null;
          }

          const language = `${item.language || ''}`.trim();
          const status = `${item.status || ''}`.trim();
          const preview = this.extractTemplatePreview(item.components || []);

          let bodyPlaceholders = this.extractTemplateBodyPlaceholders(preview.body || '');
          const declaredBodyParamsCount = Number(item.body_params_count || 0);
          if (!bodyPlaceholders.length && declaredBodyParamsCount > 0) {
            bodyPlaceholders = Array.from({ length: declaredBodyParamsCount }, (_, index) => index + 1);
          }

          const bodyParamsCount = declaredBodyParamsCount > 0
            ? declaredBodyParamsCount
            : bodyPlaceholders.length;

          let headerFormat = `${item.header_format || item.media_header_type || ''}`.trim().toUpperCase();
          if (!headerFormat && preview && preview.header && preview.header.format) {
            headerFormat = `${preview.header.format}`.trim().toUpperCase();
          }

          const hasMediaHeader = !!item.has_media_header || (headerFormat !== '' && headerFormat !== 'TEXT');

          let cardsCount = Number(item.cards_count || 0);
          if (cardsCount <= 0) {
            cardsCount = this.extractTemplateCardsCount(item.components || []);
          }

          return {
            id,
            name,
            language,
            status,
            bodyParamsCount,
            headerFormat: headerFormat || null,
            hasMediaHeader,
            cardsCount,
            preview,
            bodyPlaceholders,
            label: `${name} | status: ${status || '-'}`,
          };
        })
        .filter((item) => item && item.id && item.name)
        .sort((first, second) => {
          const byName = String(first.name).localeCompare(String(second.name), 'pt-BR');
          if (byName !== 0) {
            return byName;
          }

          return String(first.language || '').localeCompare(String(second.language || ''), 'pt-BR');
        });

      return {
        instances: instances
          .filter((item) => item && item.id && item.name)
          .map((item) => ({
            id: item.id,
            name: item.name,
            phoneNumber: item.phone_number || '',
            isOfficial: !!item.is_official,
            activated: item.activated !== false,
            label: `${item.name} | ${item.is_official ? 'Cloud' : 'Web'} | ${item.activated === false ? 'Inativa' : 'Ativa'} | fone: ${item.phone_number || '-'} (#${item.id})`,
          })),
        customerGroups: customerGroups
          .filter((item) => item && item.id && item.name)
          .map((item) => ({
            id: item.id,
            name: item.name,
            tagList: Array.isArray(item.tag_list)
              ? item.tag_list.map((tag) => `${tag || ''}`.trim()).filter((tag) => tag !== '')
              : [],
            hasDiffTagList: !!item.has_diff_tag_list,
            label: `${item.name} (#${item.id})`,
          })),
        messageTemplates: normalizedTemplates,
        chatFlows: chatFlows
          .filter((item) => item && item.id && item.description)
          .map((item) => ({
            id: item.id,
            description: item.description,
            label: `${item.description} (#${item.id})`,
          })),
      };
    },
    clearSelectorSuggestions() {
      this.selectedSuggestions = initialSelectedSuggestions();
    },
    clearSuggestedIdentifierFields() {
      this.form.instanceName = '';
      this.form.customerGroupName = '';
      this.form.messageTemplateName = '';
      this.form.chatFlowDescription = '';
      this.officialParamsValues = [];
    },
    applyDefaultSuggestions() {
      const singleInstance = this.selectorOptions.instances.length === 1
        ? this.selectorOptions.instances[0]
        : null;
      const singleGroup = this.selectorOptions.customerGroups.length === 1
        ? this.selectorOptions.customerGroups[0]
        : null;
      const singleTemplate = this.selectorOptions.messageTemplates.length === 1
        ? this.selectorOptions.messageTemplates[0]
        : null;

      if (singleInstance) {
        this.selectedSuggestions.instanceId = singleInstance.id;
        this.applyInstanceSuggestion(singleInstance.id);
      }

      if (singleGroup) {
        this.selectedSuggestions.customerGroupId = singleGroup.id;
        this.applyCustomerGroupSuggestion(singleGroup.id);
      }

      if (singleTemplate && this.isReferenceInstanceOfficial()) {
        this.selectedSuggestions.messageTemplateId = singleTemplate.id;
        this.applyMessageTemplateSuggestion(singleTemplate.id);
      }
    },
    applyInstanceSuggestion(selectedId) {
      const option = this.selectorOptions.instances.find((item) => Number(item.id) === Number(selectedId));
      this.form.instanceName = option && option.name ? option.name : '';

      if (option && option.isOfficial === false) {
        this.selectedSuggestions.messageTemplateId = null;
        this.form.messageTemplateName = '';
        this.officialParamsValues = [];
      }
    },
    isReferenceInstanceOfficialById(instanceId) {
      const parsedInstanceId = Number(instanceId);
      if (Number.isNaN(parsedInstanceId)) {
        return false;
      }

      const option = this.selectorOptions.instances.find((item) => Number(item.id) === parsedInstanceId);
      return !!(option && option.isOfficial);
    },
    async applyReferenceTemplateFilterOnFirstFetch(options = {}) {
      const { useLoading = true } = options;

      const tenantName = this.referenceTenantName;
      if (!tenantName || !this.referenceOptionsLoaded) {
        return;
      }

      if (!this.showCreateFlow || this.currentStep !== 2) {
        return;
      }

      const instanceId = Number(this.selectedSuggestions.instanceId);
      if (Number.isNaN(instanceId) || instanceId <= 0) {
        this.referenceTemplateFilteredInstanceId = null;
        return;
      }

      if (!this.isReferenceInstanceOfficialById(instanceId)) {
        this.referenceTemplateFilteredInstanceId = null;
        return;
      }

      if (Number(this.referenceTemplateFilteredInstanceId) === instanceId) {
        return;
      }

      if (useLoading) {
        if (this.loadingOptions) {
          return;
        }
        this.loadingOptions = true;
      }

      try {
        const response = await this.$store.dispatch('campaignMassOptions', {
          tenantName,
          token: this.token,
          instanceId,
        });

        const normalized = this.normalizeTenantOptionsPayload(response.data || {});
        this.optionsCacheByTenant = {
          ...this.optionsCacheByTenant,
          [tenantName]: normalized,
        };
        this.selectorOptions = normalized;

        const selectedTemplateId = Number(this.selectedSuggestions.messageTemplateId);
        if (!Number.isNaN(selectedTemplateId)) {
          const stillAvailable = normalized.messageTemplates.some((item) => Number(item.id) === selectedTemplateId);
          if (!stillAvailable) {
            this.selectedSuggestions.messageTemplateId = null;
            this.form.messageTemplateName = '';
            this.officialParamsValues = [];
          }
        }

        this.referenceTemplateFilteredInstanceId = instanceId;
        this.sanitizeOfficialParamsValues();
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao filtrar modelos pela instância do tenant modelo.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        if (useLoading) {
          this.loadingOptions = false;
        }
      }
    },
    applyCustomerGroupSuggestion(selectedId) {
      const option = this.selectorOptions.customerGroups.find((item) => Number(item.id) === Number(selectedId));
      this.form.customerGroupName = option && option.name ? option.name : '';
      this.sanitizeOfficialParamsValues();
    },
    applyMessageTemplateSuggestion(selectedId) {
      const option = this.selectorOptions.messageTemplates.find((item) => Number(item.id) === Number(selectedId));
      this.form.messageTemplateName = option && option.name ? option.name : '';

      if (!option) {
        this.officialParamsValues = [];
        return;
      }

      const requiredCount = Number(option.bodyParamsCount || 0);
      if (requiredCount <= 0) {
        this.officialParamsValues = [];
        return;
      }

      const current = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];
      this.officialParamsValues = Array.from({ length: requiredCount }, (_, index) => (current[index] || ''));
      this.sanitizeOfficialParamsValues();
    },
    applyChatFlowSuggestion(selectedId) {
      const option = this.selectorOptions.chatFlows.find((item) => Number(item.id) === Number(selectedId));
      this.form.chatFlowDescription = option && option.description ? option.description : '';
    },
    async loadReferenceOptions() {
      if (!this.referenceTenantId) {
        this.$vs.notify({ color: 'warning', title: 'Seleção obrigatória', text: 'Selecione um tenant de referência.' });
        return;
      }

      const tenantName = this.resolveTenantNameById(this.referenceTenantId);
      if (!tenantName) {
        this.$vs.notify({ color: 'danger', title: 'Erro', text: 'Não foi possível resolver o tenant de referência.' });
        return;
      }

      this.loadingOptions = true;

      try {
        this.referenceTemplateFilteredInstanceId = null;

        if (this.optionsCacheByTenant[tenantName]) {
          this.selectorOptions = this.optionsCacheByTenant[tenantName];
        } else {
          const response = await this.$store.dispatch('campaignMassOptions', {
            tenantName,
            token: this.token,
          });

          const normalized = this.normalizeTenantOptionsPayload(response.data || {});
          this.optionsCacheByTenant = {
            ...this.optionsCacheByTenant,
            [tenantName]: normalized,
          };
          this.selectorOptions = normalized;
        }

        this.clearSelectorSuggestions();
        this.clearSuggestedIdentifierFields();
        this.applyDefaultSuggestions();
        this.lastLoadedTenantName = tenantName;
        this.syncTenantInstanceOverrides();
        this.syncTenantCustomerGroupOverrides();
        this.sanitizeOfficialParamsValues();

        await this.loadSelectedTenantsInstanceOptions();

        this.$vs.notify({
          color: 'success',
          title: 'Sugestões carregadas',
          text: `Instâncias: ${this.selectorOptions.instances.length}, Grupos: ${this.selectorOptions.customerGroups.length}, Modelos: ${this.selectorOptions.messageTemplates.length}, Fluxos: ${this.selectorOptions.chatFlows.length}`,
        });
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao carregar sugestões do tenant.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        this.loadingOptions = false;
      }
    },
    onTenantsSelected(selected) {
      this.form.tenantIds = Array.isArray(selected) ? selected.map((id) => Number(id)).filter((id) => !Number.isNaN(id)) : [];
      const selectedIds = new Set(this.form.tenantIds.map((id) => Number(id)));
      const nextOverrides = {};

      Object.entries(this.tenantInstanceOverridesByTenantId || {}).forEach(([tenantId, value]) => {
        const parsedTenantId = Number(tenantId);
        if (!Number.isNaN(parsedTenantId) && selectedIds.has(parsedTenantId)) {
          nextOverrides[parsedTenantId] = value;
        }
      });
      this.tenantInstanceOverridesByTenantId = nextOverrides;

      const nextCustomerGroupOverrides = {};
      Object.entries(this.tenantCustomerGroupOverridesByTenantId || {}).forEach(([tenantId, value]) => {
        const parsedTenantId = Number(tenantId);
        if (!Number.isNaN(parsedTenantId) && selectedIds.has(parsedTenantId)) {
          nextCustomerGroupOverrides[parsedTenantId] = value;
        }
      });
      this.tenantCustomerGroupOverridesByTenantId = nextCustomerGroupOverrides;

      if (this.referenceTenantId !== null && this.referenceTenantId !== undefined) {
        const referenceId = Number(this.referenceTenantId);
        const hasReference = !Number.isNaN(referenceId) && this.form.tenantIds.includes(referenceId);

        if (!hasReference) {
          this.referenceTenantId = null;
          this.referenceTemplateFilteredInstanceId = null;
        }
      }

      if (!this.form.tenantIds.length) {
        this.lastLoadedTenantName = null;
        this.referenceTemplateFilteredInstanceId = null;
        this.selectorOptions = initialSelectorOptions();
        this.clearSelectorSuggestions();
      }

      this.syncTenantInstanceOverrides();
      this.syncTenantCustomerGroupOverrides();

      if (this.referenceOptionsLoaded) {
        this.loadSelectedTenantsInstanceOptions();
      }
    },
    validateForm() {
      if (!this.form.name) {
        throw new Error('Nome da campanha é obrigatório.');
      }

      if (!this.form.instanceName) {
        throw new Error('Nome da instância é obrigatório.');
      }

      if (!this.form.customerGroupName) {
        throw new Error('Nome do grupo de clientes é obrigatório.');
      }

      if (this.form.useChatFlow && !this.form.chatFlowDescription) {
        throw new Error('Descrição do fluxo é obrigatória quando envio por fluxo estiver ativo.');
      }

      if (this.isReferenceInstanceOfficial() && !this.form.messageTemplateName) {
        throw new Error('Modelo de Mensagem é obrigatório para conexões Cloud.');
      }

      if (this.isReferenceInstanceOfficial() && !this.selectedSuggestions.messageTemplateId) {
        throw new Error('Selecione o modelo na lista de sugestões do tenant modelo.');
      }

      if (this.isReferenceInstanceOfficial() && this.templateHasCarousel) {
        throw new Error('Modelos com carrossel ainda não são suportados na criação em massa.');
      }

      const requiredTemplateHeaderFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      if (this.isReferenceInstanceOfficial() && requiredTemplateHeaderFormat !== '' && requiredTemplateHeaderFormat !== 'TEXT') {
        const imageUrl = `${this.form.imageUrl || ''}`.trim();
        if (imageUrl === '') {
          throw new Error('O modelo selecionado exige mídia no cabeçalho. Faça upload de um arquivo.');
        }

        const providedFormat = this.resolveCurrentImageMediaFormat();
        if (!providedFormat) {
          throw new Error('Não foi possível identificar o tipo da mídia informada para o cabeçalho do modelo.');
        }

        if (providedFormat !== requiredTemplateHeaderFormat) {
          throw new Error(`A mídia informada é ${providedFormat}, porém o modelo exige ${requiredTemplateHeaderFormat}.`);
        }
      }

      if (this.isReferenceInstanceOfficial() && this.requiredTemplateParamsCount > 0) {
        if (!this.referenceGroupTagOptions.length) {
          throw new Error('O grupo de clientes de referência não possui campos para mapear as variáveis do modelo.');
        }

        const params = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];

        if (params.length !== this.requiredTemplateParamsCount) {
          throw new Error(`Mapeie os ${this.requiredTemplateParamsCount} parâmetros do modelo para campos do grupo de clientes.`);
        }

        const hasEmpty = params.some((item) => `${item || ''}`.trim() === '');
        if (hasEmpty) {
          throw new Error('Todos os parâmetros do modelo devem ser mapeados para um campo do grupo de clientes.');
        }
      }

      if (!this.form.tenantIds.length) {
        throw new Error('Selecione ao menos uma dash.');
      }

      this.validateStepTwoMappings();
    },
    isReferenceInstanceOfficial() {
      return this.referenceInstanceOfficialFlag === true;
    },
    buildInstanceOverridesPayload() {
      return this.tenantInstanceRows
        .map((row) => {
          if (!row || !row.selectedInstanceName) {
            return null;
          }

          return {
            tenantId: row.tenantId,
            instanceName: row.selectedInstanceName,
            instanceIsOfficial: row.selectedIsOfficial,
          };
        })
        .filter((item) => item && item.tenantId && item.instanceName);
    },
    buildCustomerGroupOverridesPayload() {
      return this.tenantCustomerGroupRows
        .map((row) => {
          if (!row || !row.selectedCustomerGroupName) {
            return null;
          }

          return {
            tenantId: row.tenantId,
            customerGroupName: row.selectedCustomerGroupName,
          };
        })
        .filter((item) => item && item.tenantId && item.customerGroupName);
    },
    buildPayload() {
      this.validateForm();

      return {
        name: this.form.name,
        type: 0,
        tenantIds: this.form.tenantIds,
        instanceName: this.form.instanceName,
        instanceIsOfficial: this.referenceInstanceOfficialFlag,
        instanceOverrides: this.buildInstanceOverridesPayload(),
        customerGroupName: this.form.customerGroupName,
        customerGroupOverrides: this.buildCustomerGroupOverridesPayload(),
        messageTemplateName: this.form.messageTemplateName || null,
        useChatFlow: this.form.useChatFlow,
        chatFlowDescription: this.form.chatFlowDescription || null,
        startAt: this.form.startAt || null,
        finishAt: this.form.finishAt || null,
        message: this.form.message || null,
        firstMessage: this.form.firstMessage || null,
        imageUrl: this.form.imageUrl || null,
        imageCaption: this.form.imageCaption || null,
        imageDescription: this.form.imageDescription || null,
        officialParams: this.isReferenceInstanceOfficial() ? (Array.isArray(this.officialParamsValues) ? this.officialParamsValues.map((value) => `${value || ''}`.trim()) : []) : [],
        components: null,
        sendOptOut: this.form.sendOptOut,
        setCaption: this.form.setCaption,
        updateAllInSheet: this.form.updateAllInSheet,
        isHumanized: this.form.isHumanized,
      };
    },
    async runDryRun() {
      this.loadingDryRun = true;

      try {
        const payload = this.buildPayload();
        const response = await this.$store.dispatch('campaignMassDryRun', {
          payload,
          token: this.token,
        });

        this.dryRunBatch = response.data;

        this.$vs.notify({
          color: 'success',
          title: 'Pré-validação concluída',
          text: `Lote #${response.data.id} validado. Prontos: ${response.data.ready_tenants}/${response.data.total_tenants}`,
        });

        await this.fetchHistory();
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao validar lote.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        this.loadingDryRun = false;
      }
    },
    async executeBatch() {
      if (!this.dryRunBatch || !this.dryRunBatch.id) {
        return;
      }

      this.loadingExecute = true;

      try {
        const response = await this.$store.dispatch('campaignMassExecute', {
          batchId: this.dryRunBatch.id,
          token: this.token,
        });
        const executedBatchId = response.data.id;

        this.dryRunBatch = response.data;

        this.$vs.notify({
          color: 'success',
          title: 'Lote enfileirado',
          text: `Lote #${response.data.id} em processamento.`,
        });

        this.cancelCreateFlow();
        await this.fetchHistory();
        await this.showBatchDetail(executedBatchId, true);
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao executar lote.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      } finally {
        this.loadingExecute = false;
      }
    },
    async fetchHistory() {
      try {
        const response = await this.$store.dispatch('campaignMassHistory', {
          page: 1,
          limit: 20,
          status: this.filters.status || null,
          requestedBy: null,
          tenantId: null,
          from: this.filters.from,
          to: this.filters.to,
          token: this.token,
        });

        this.history = response.data;
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao carregar histórico.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      }
    },
    async showBatchDetail(batchId, forceOpen = false) {
      const parsedBatchId = Number(batchId);
      const currentBatchId = Number(this.selectedBatchDetail && this.selectedBatchDetail.id);

      if (
        !forceOpen
        && !Number.isNaN(parsedBatchId)
        && !Number.isNaN(currentBatchId)
        && parsedBatchId === currentBatchId
      ) {
        this.selectedBatchDetail = null;
        return;
      }

      try {
        const response = await this.$store.dispatch('campaignMassHistoryDetail', {
          batchId: parsedBatchId,
          token: this.token,
        });

        this.selectedBatchDetail = response.data;
      } catch (error) {
        const message = this.extractErrorMessage(error, 'Falha ao carregar detalhe do lote.');
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      }
    },
    resetHistoryFilters() {
      this.filters.status = '';
      this.filters.from = null;
      this.filters.to = null;
      this.fetchHistory();
    },
  },
  watch: {
    referenceTenantId: async function (value, previousValue) {
      if (!this.showCreateFlow || this.currentStep !== 1) {
        return;
      }

      if (value !== previousValue) {
        this.referenceTemplateFilteredInstanceId = null;
      }

      if (!value || value === previousValue || this.loadingOptions) {
        return;
      }

      const parsedReferenceId = Number(value);
      if (Number.isNaN(parsedReferenceId)) {
        return;
      }

      if (!Array.isArray(this.form.tenantIds) || !this.form.tenantIds.includes(parsedReferenceId)) {
        return;
      }

      if (this.referenceOptionsLoaded) {
        return;
      }

      await this.loadReferenceOptions();
    },
    currentStep: async function (value) {
      if (value !== 2 || !this.showCreateFlow) {
        return;
      }

      await this.loadSelectedTenantsInstanceOptions();
      await this.applyReferenceTemplateFilterOnFirstFetch();
    },
    'selectedSuggestions.instanceId': async function () {
      this.syncTenantInstanceOverrides();

      if (!this.showCreateFlow || this.currentStep !== 2) {
        return;
      }

      await this.applyReferenceTemplateFilterOnFirstFetch();
    },
    'selectedSuggestions.customerGroupId': function () {
      this.syncTenantCustomerGroupOverrides();
      this.sanitizeOfficialParamsValues();
    },
    'selectedSuggestions.messageTemplateId': function () {
      this.sanitizeOfficialParamsValues();
    },
    'form.useChatFlow': function (value) {
      if (value) {
        this.form.sendOptOut = false;
        this.form.setCaption = false;
        this.form.isHumanized = false;
      }
    },
  },
  async created() {
    await this.fetchHistory();
  },
};
</script>

<style lang="scss" scoped>
.mass-campaign-page {
  .wizard-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .wizard-hint {
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
  }
}
</style>
